import LANG from '../lang';

const supportedLanguages = Object.values(LANG).map(({ lng }) => lng);
const GOOGLE_TRANSLATE_STORAGE_KEY = 'google-translate-language';
const GOOGLE_TRANSLATE_COOKIE_NAME = 'googtrans';
const COOKIE_EXPIRES_AT = 'Thu, 01 Jan 1970 00:00:00 GMT';

export const getSupportedLanguage = (language) => {
	if (!language) return LANG.EN.lng;

	const normalizedLanguage = String(language).toLowerCase();
	if (supportedLanguages.includes(normalizedLanguage)) return normalizedLanguage;

	const baseLanguage = normalizedLanguage.split('-')[0];
	if (supportedLanguages.includes(baseLanguage)) return baseLanguage;

	return LANG.EN.lng;
};

const getCookieDomainCandidates = () => {
	if (typeof window === 'undefined') return [];

	const hostname = window.location.hostname;
	if (!hostname || hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
		return [];
	}

	const parts = hostname.split('.');
	const domains = new Set([hostname, `.${hostname}`]);

	for (let index = 1; index < parts.length - 1; index += 1) {
		const parentDomain = parts.slice(index).join('.');
		domains.add(parentDomain);
		domains.add(`.${parentDomain}`);
	}

	return Array.from(domains);
};

const getCookiePathCandidates = () => {
	if (typeof window === 'undefined') return ['/'];

	const paths = new Set(['/']);
	const segments = window.location.pathname.split('/').filter(Boolean);
	let currentPath = '';

	segments.forEach((segment) => {
		currentPath += `/${segment}`;
		paths.add(currentPath);
	});

	return Array.from(paths);
};

const writeGoogleTranslateCookie = (value, options = {}) => {
	const cookieValue = value || '';
	const expires = options.expires ? `;expires=${options.expires}` : '';
	const path = options.path || '/';
	const domain = options.domain ? `;domain=${options.domain}` : '';
	document.cookie = `${GOOGLE_TRANSLATE_COOKIE_NAME}=${cookieValue};path=${path}${domain}${expires}`;
};

const clearGoogleTranslateCookies = () => {
	const paths = getCookiePathCandidates();
	const domains = getCookieDomainCandidates();

	paths.forEach((path) => {
		writeGoogleTranslateCookie('', { expires: COOKIE_EXPIRES_AT, path });

		domains.forEach((domain) => {
			writeGoogleTranslateCookie('', { domain, expires: COOKIE_EXPIRES_AT, path });
		});
	});
};

const setGoogleTranslateCookie = (lang) => {
	clearGoogleTranslateCookies();

	if (lang === LANG.EN.lng) {
		return;
	}

	const cookieValue = `/en/${lang}`;
	writeGoogleTranslateCookie(cookieValue);

	getCookieDomainCandidates().forEach((domain) => {
		writeGoogleTranslateCookie(cookieValue, { domain });
	});
};

const getGoogleTranslateCookieLanguage = () => {
	const matchedCookies = document.cookie
		.split(';')
		.map((cookie) => cookie.trim())
		.filter((cookie) => cookie.startsWith(`${GOOGLE_TRANSLATE_COOKIE_NAME}=`));

	if (matchedCookies.length === 0) {
		return LANG.EN.lng;
	}

	const languages = matchedCookies.map((cookie) => {
		const cookieValue = decodeURIComponent(cookie.split('=')[1] || '');
		const targetLanguage = cookieValue.split('/').pop();
		return getSupportedLanguage(targetLanguage);
	});

	return languages.find((language) => language !== LANG.EN.lng) || LANG.EN.lng;
};

const hasGoogleTranslatedDom = () => {
	if (typeof document === 'undefined') return false;

	return (
		document.documentElement.className.includes('translated') ||
		document.body?.className.includes('translated')
	);
};

export const syncGoogleTranslateLanguage = async (language) => {
	const nextLanguage = getSupportedLanguage(language);
	localStorage.setItem(GOOGLE_TRANSLATE_STORAGE_KEY, nextLanguage);
	setGoogleTranslateCookie(nextLanguage);
	return nextLanguage;
};

export const changeGoogleTranslateLanguage = async (language) => {
	const nextLanguage = getSupportedLanguage(language);
	const currentLanguage = getGoogleTranslateCookieLanguage();
	const wasTranslated = hasGoogleTranslatedDom() || currentLanguage !== LANG.EN.lng;
	console.log('Changing language to:', nextLanguage);

	await syncGoogleTranslateLanguage(nextLanguage);

	if (currentLanguage !== nextLanguage || (nextLanguage === LANG.EN.lng && wasTranslated)) {
		window.setTimeout(() => {
			window.location.reload();
		}, 150);
	}

	return nextLanguage;
};

export const getStoredLanguage = () => {
	return getSupportedLanguage(localStorage.getItem(GOOGLE_TRANSLATE_STORAGE_KEY));
};

export const isGoogleTranslateActive = () =>
	getStoredLanguage() !== LANG.EN.lng ||
	getGoogleTranslateCookieLanguage() !== LANG.EN.lng ||
	hasGoogleTranslatedDom();
